import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  ListItem,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SavedSearchIcon from '@mui/icons-material/SavedSearch'
import ReactMarkdown from 'react-markdown'
import type { ScanResponse } from '@/models/Scans'
import { getOpportunityDisplayInfo } from '@/utils/dateUtils'
import { COLORS } from '../theme'
import { primaryButtonSx, outlinedButtonSx } from '../styles'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import AccessibleTooltip from '@/components/AccessibleTooltip'

interface OnboardingMatchRowProps {
  match: ScanResponse
  selected: boolean
  selectable: boolean
  isFuturePlanning?: boolean
  onSelect: () => void
}

function renderTextWithLinks(text: string) {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g
  const parts = text.split(urlRegex)

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      const trailingPunctuationMatch = part.match(/([.,;:!?]+)$/)
      const trailingPunctuation = trailingPunctuationMatch
        ? trailingPunctuationMatch[0]
        : ''
      const cleanUrl = trailingPunctuation
        ? part.slice(0, -trailingPunctuation.length)
        : part
      const href = cleanUrl.startsWith('www.')
        ? `https://${cleanUrl}`
        : cleanUrl

      return (
        <React.Fragment key={index}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {part}
          </a>
        </React.Fragment>
      )
    }
    return part
  })
}

export default function OnboardingMatchRow({
  match,
  selected,
  selectable,
  isFuturePlanning = false,
  onSelect,
}: OnboardingMatchRowProps) {
  const [showResultDetails, setShowResultDetails] = useState(false)
  const [showOppDetails, setShowOppDetails] = useState(false)

  const displayInfo = getOpportunityDisplayInfo(
    match.opportunity,
    match.application_open_close_dates,
  )

  const funderName = match.funder?.name || 'Unknown Funder'
  const fundName = match.opportunity?.fund_name

  function handleShowResultDetails() {
    setShowResultDetails(!showResultDetails)
    if (showOppDetails) setShowOppDetails(false)
  }

  function handleShowOppDetails() {
    setShowOppDetails(!showOppDetails)
    if (showResultDetails) setShowResultDetails(false)
  }

  return (
    <Box
      sx={{
        border: selected ? 2 : 0,
        borderColor: selected ? COLORS.primary : 'transparent',
        borderRadius: selected ? 2 : 0,
        bgcolor: selected ? COLORS.selectedBg : 'white',
      }}
    >
      <ListItem
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          py: 3,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Top row: title + select button */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: '#1C3045',
                maxWidth: '400px',
                fontSize: '15px',
              }}
            >
              {funderName}
              {displayInfo.status === 'closed' &&
                (isFuturePlanning ? (
                  <Chip
                    label="Upcoming dates not confirmed"
                    size="small"
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '11px',
                      border: '1px solid',
                      borderColor: 'warning.main',
                      bgcolor: 'white',
                      color: 'warning.main',
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Chip
                    label="Closed"
                    size="small"
                    sx={{
                      ml: 1,
                      height: '20px',
                      fontSize: '11px',
                      bgcolor: 'error.main',
                      color: 'white',
                      fontWeight: 600,
                    }}
                  />
                ))}
              {displayInfo.status === 'closing-soon' && (
                <Chip
                  label="Closing Soon"
                  size="small"
                  sx={{
                    ml: 1,
                    height: '20px',
                    fontSize: '11px',
                    bgcolor: 'warning.main',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
            </Typography>
            {fundName && (
              <Typography
                variant="body2"
                sx={{
                  color: '#1C3045',
                  fontWeight: 400,
                  fontSize: '15px',
                  mt: -0.5,
                }}
              >
                {fundName}
              </Typography>
            )}

            {/* Admin Notes */}
            {match.notes && (
              <Box
                sx={{
                  bgcolor: '#e8f5e8',
                  border: '1px solid #c8e6c9',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  display: 'inline-block',
                }}
              >
                <Typography variant="body2">{match.notes}</Typography>
              </Box>
            )}
          </Box>

          {selectable && !match.opportunity.has_questions ? (
            <Alert
              severity="info"
              icon={false}
              sx={{ width: '200px', textAlign: 'center' }}
            >
              No application questions
            </Alert>
          ) : selectable ? (
            <Button
              disableRipple
              variant={selected ? 'contained' : 'outlined'}
              size="small"
              onClick={onSelect}
              sx={{
                ...(selected ? primaryButtonSx : outlinedButtonSx),
                minWidth: 80,
                flexShrink: 0,
              }}
            >
              {selected ? 'Selected' : 'Select'}
            </Button>
          ) : match.opportunity.is_relational ? (
            <AccessibleTooltip title="Not available during onboarding">
              <Button
                variant="contained"
                disableRipple
                aria-disabled="true"
                onClick={(e) => e.preventDefault()}
                sx={{
                  color: 'white',
                  bgcolor: '#1C3045',
                  textTransform: 'none',
                  opacity: 0.38,
                  cursor: 'default',
                  '&:hover': { bgcolor: '#1C3045' },
                }}
              >
                Draft Introduction Letter
              </Button>
            </AccessibleTooltip>
          ) : null}
        </Box>

        {/* Action buttons row — hidden for relational matches during onboarding */}

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Button
            disableRipple
            onClick={handleShowResultDetails}
            aria-expanded={showResultDetails}
            aria-controls={`match-details-${match.id}`}
            sx={{
              textTransform: 'none',
              fontSize: '16px',
              color: '#1C3045',
              borderColor: '#1C3045',
              textDecoration: 'none',
              minWidth: '270px',
              maxHeight: '40px',
              borderRadius: 5,
              border: 1,
              bgcolor:
                !showOppDetails && showResultDetails
                  ? '#eeeeee'
                  : 'transparent',
            }}
            variant="contained"
          >
            See how we matched
            <SavedSearchIcon sx={{ ml: 1 }} />
            {!showOppDetails && showResultDetails ? (
              <ExpandLessIcon sx={{ ml: 1 }} />
            ) : (
              <ExpandMoreIcon sx={{ ml: 1 }} />
            )}
          </Button>
          <Button
            disableRipple
            onClick={handleShowOppDetails}
            aria-expanded={showOppDetails}
            aria-controls={`funder-details-${match.id}`}
            sx={{
              textTransform: 'none',
              fontSize: '16px',
              color: '#1C3045',
              borderColor: '#1C3045',
              minWidth: '250px',
              maxHeight: '40px',
              textDecoration:
                showOppDetails && !showResultDetails ? 'underline' : 'none',
              ':hover': {
                textDecoration: 'underline',
              },
              border: 0,
              bgcolor: 'transparent',
              '&:focus': {
                backgroundColor: '#e8e8e8',
              },
            }}
            variant="text"
          >
            {showOppDetails && !showResultDetails ? 'Hide' : 'View'} funder
            information
            {showOppDetails && !showResultDetails ? (
              <ExpandLessIcon sx={{ ml: 1 }} />
            ) : (
              <ExpandMoreIcon sx={{ ml: 1 }} />
            )}
          </Button>
        </Box>
      </ListItem>

      {/* Match details (expanded) */}
      {showResultDetails && (
        <Box id={`match-details-${match.id}`} sx={{ px: 4, py: 2 }}>
          {/* Opportunity status alert */}
          {displayInfo.status &&
            (isFuturePlanning && displayInfo.status === 'closed' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Round dates not confirmed — this funder has run rounds
                  recently and may open again
                </Typography>
              </Alert>
            ) : displayInfo.message ? (
              <Alert
                severity={displayInfo.status === 'closed' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {displayInfo.message}
                </Typography>
              </Alert>
            ) : null)}

          <Typography
            variant="body1"
            sx={{ mt: 1, fontSize: '16px', color: '#1C3045' }}
          >
            {match.result}
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 2, fontSize: '16px', color: '#1C3045' }}
          >
            <span style={{ fontWeight: '500' }}>Suitability:</span> {match.rank}{' '}
            / 5
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mt: 2,
              fontSize: '16px',
              color: '#1C3045',
              fontWeight: '500',
            }}
          >
            {displayInfo.status === 'closed'
              ? 'Previous Application Dates:'
              : 'Application Dates:'}
          </Typography>
          <Typography
            variant="body1"
            sx={{ mt: 1, fontSize: '16px', color: '#1C3045' }}
            component="div"
          >
            {match.opportunity.always_open
              ? 'Always open'
              : match.application_open_close_dates
                  .filter(
                    (date, index, self) =>
                      index ===
                      self.findIndex(
                        (d) =>
                          d.application_open === date.application_open &&
                          d.application_close === date.application_close,
                      ),
                  )
                  .filter((date) => {
                    if (displayInfo.status === 'closed') return true
                    if (!date.application_close) return true
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    const closeDate = new Date(date.application_close)
                    closeDate.setHours(0, 0, 0, 0)
                    return closeDate >= today
                  })
                  .sort((a, b) => {
                    if (!a.application_open || !b.application_open) return 0
                    return (
                      new Date(a.application_open).getTime() -
                      new Date(b.application_open).getTime()
                    )
                  })
                  .map((date, index) => {
                    const openDate =
                      date.application_open && new Date(date.application_open)
                    const closeDate =
                      date.application_close && new Date(date.application_close)

                    const formattedOpenDate =
                      openDate instanceof Date
                        ? openDate.toLocaleDateString('en-NZ', {
                            month: 'long',
                            day: '2-digit',
                            year: 'numeric',
                          })
                        : 'Not provided'
                    const formattedCloseDate =
                      closeDate instanceof Date
                        ? closeDate.toLocaleDateString('en-NZ', {
                            month: 'long',
                            day: '2-digit',
                            year: 'numeric',
                          })
                        : 'Not provided'

                    const isEstimate = date.estimate_date_flag || false

                    return (
                      <React.Fragment key={index}>
                        {index === 0 && (
                          <Divider sx={{ mt: 1, mb: 1, maxWidth: '250px' }} />
                        )}
                        <li>
                          <span
                            style={{
                              fontWeight: '500',
                              marginRight: 20,
                              marginLeft: 5,
                            }}
                          >
                            Open{' '}
                          </span>{' '}
                          {formattedOpenDate}
                        </li>
                        <li>
                          <span
                            style={{
                              fontWeight: '500',
                              marginRight: 19.5,
                              marginLeft: 5,
                            }}
                          >
                            Close
                          </span>{' '}
                          {formattedCloseDate}
                        </li>
                        {isEstimate && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              my: 0.5,
                              gap: 0.5,
                              ml: 1,
                            }}
                            key={'is-estimate-' + index}
                          >
                            <AccessibleTooltip title="These round dates are estimated. They will be confirmed when more information is available.">
                              <span
                                role="img"
                                aria-label="Info: These round dates are estimated. They will be confirmed when more information is available."
                                tabIndex={0}
                              >
                                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                              </span>
                            </AccessibleTooltip>
                            <Typography variant="body2" fontStyle="italic">
                              Estimate Only
                            </Typography>
                          </Box>
                        )}
                        <Divider sx={{ mt: 1, mb: 1, maxWidth: '250px' }} />
                      </React.Fragment>
                    )
                  })}
          </Typography>

          {match.questions && (
            <>
              <Typography
                variant="body1"
                sx={{
                  mt: 3,
                  fontSize: '16px',
                  color: '#1C3045',
                  fontWeight: '500',
                }}
              >
                Points you may want to address in your application:
              </Typography>
              {match.questions.split('?').map(
                (question, index) =>
                  question.trim() !== '' && (
                    <Typography key={index} variant="body1" sx={{ mt: 1 }}>
                      {/^\d+\./.test(question.trim())
                        ? question.trim()
                        : index + 1 + '. ' + question.trim()}
                      ?
                    </Typography>
                  ),
              )}
            </>
          )}
        </Box>
      )}

      {/* Funder details (expanded) */}
      {showOppDetails && (
        <Box id={`funder-details-${match.id}`} sx={{ px: 4, pt: 0.5, pb: 5 }}>
          {/* Opportunity status alert */}
          {displayInfo.status &&
            (isFuturePlanning && displayInfo.status === 'closed' ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Upcoming Funding Round dates not confirmed — this funder has
                  run rounds recently and may open again
                </Typography>
              </Alert>
            ) : displayInfo.message ? (
              <Alert
                severity={displayInfo.status === 'closed' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {displayInfo.message}
                </Typography>
              </Alert>
            ) : null)}

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="body1"
                sx={{ mt: 2, fontSize: '16px', color: '#1C3045' }}
              >
                <span style={{ fontWeight: '500' }}>Funder:</span> {funderName}
              </Typography>
              <Typography
                variant="body1"
                sx={{ mt: 0.5, fontSize: '16px', color: '#1C3045' }}
              >
                {(() => {
                  const link =
                    match.opportunity.opportunity_link || match.funder?.website
                  const label = match.opportunity.opportunity_link
                    ? 'View Opportunity:'
                    : 'Website:'

                  if (link) {
                    return (
                      <>
                        <span style={{ fontWeight: '500' }}>{label}</span>{' '}
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1C3045' }}
                        >
                          {link}
                        </a>
                      </>
                    )
                  }

                  return (
                    <>
                      <span style={{ fontWeight: '500' }}>Website:</span> None
                      Provided
                    </>
                  )
                })()}
              </Typography>
              {match.opportunity?.general_info && (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.5,
                      fontSize: '16px',
                      color: '#1C3045',
                      fontWeight: '500',
                    }}
                  >
                    Useful Info:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ whiteSpace: 'pre-wrap', mt: 1, color: '#1C3045' }}
                  >
                    {renderTextWithLinks(match.opportunity.general_info)}
                  </Typography>
                </>
              )}
            </Box>
          </Box>

          {match.opportunity?.profile_text && (
            <>
              <Typography
                variant="body1"
                sx={{
                  mt: 3,
                  fontSize: '20px',
                  color: '#1C3045',
                  fontWeight: '500',
                }}
              >
                Opportunity Details
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  color: '#1C3045',
                  '& ol': { mt: 1 },
                  '& ul': { mt: 1 },
                }}
                component="div"
              >
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    h2: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    h3: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    h4: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    h5: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    h6: ({ children }) => (
                      <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {children}
                      </Typography>
                    ),
                    p: ({ children }) => (
                      <Typography
                        variant="body1"
                        sx={{ my: 3, mb: 0, whiteSpace: 'pre-wrap' }}
                      >
                        {children}
                      </Typography>
                    ),
                  }}
                >
                  {match.opportunity.profile_text}
                </ReactMarkdown>
              </Typography>
            </>
          )}
        </Box>
      )}

      {selected && (
        <Box
          sx={{
            mx: 2,
            mb: 2,
            pt: 1.5,
            borderTop: `1px solid ${COLORS.border}`,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: COLORS.primary, fontWeight: 500, textAlign: 'center' }}
          >
            Selected — click Continue to see your first draft
          </Typography>
        </Box>
      )}

      <Divider />
    </Box>
  )
}
